import { companyApi, CompanyVo } from '@/axios/company';
import { EUserRoleCode } from '@/axios/user';
import UserSelector from '@/components/Form/UserSlector';
import CommonModalFactory from '@/plugins/ModalFactory';
import { useRequest } from 'ahooks';
import { AutoComplete, Button, DatePicker, Form, Input, message, Select } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const EditCompany = () => {
  return <div>Edit Company Page</div>;
};

export default EditCompany;

const companyStatusOptions = [
  { label: '首次交付 - 交付中', value: 'DELIVERING' },
  { label: '首次交付 - 待验收', value: 'FIRST_WAIT_ACCEPTANCE' },
  { label: '首次交付 - 已超期', value: 'FIRST_OVERDUE' },
  { label: '首次交付 - 已上线', value: 'ONLINE' },
  { label: '首次交付 - 已上线+继续优化', value: 'FIRST_ONLINE_OPTIMIZING' },
  { label: '补词优化 - 交付中', value: 'SUPPLEMENT_DELIVERING' },
  { label: '补词优化 - 待验收', value: 'SUPPLEMENT_WAIT_ACCEPTANCE' },
  { label: '补词优化 - 未达标', value: 'SUPPLEMENT_NOT_STANDARD' },
  { label: '补词优化 - 已优化', value: 'SUPPLEMENT_OPTIMIZED' },
  { label: '测试', value: 'TESTING' },
  { label: '停止交付', value: 'STOPPED' },
];

const quickParseFieldAliases: Record<string, string[]> = {
  companyName: ['companyName', 'company_name', '企业名称', '公司名称', '名称'],
  brand: ['brand', '品牌'],
  combo: ['combo', '套餐类型', '套餐'],
  customerType: ['customerType', 'customer_type', '客户类型'],
  industry: ['industry', '行业'],
  provider: ['provider', '服务商', '供应商'],
  status: ['status', '企业状态', '状态'],
  expireTime: ['expireTime', 'expire_time', '到期时间'],
  spUserId: ['spUserId', 'sp_user_id', '服务商账号', '服务商账号ID'],
  apUserId: ['apUserId', 'ap_user_id', '代理商账号', '代理商账号ID'],
  remark: ['remark', '备注'],
};

const getQuickParseValue = (source: Record<string, any>, field: string) => {
  const aliases = quickParseFieldAliases[field] || [field];
  for (const key of aliases) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

const toAutoCompleteOptions = (values?: string[]) => (values || []).map((value) => ({ label: value, value }));

const filterOption = (inputValue: string, option?: { value?: string }) =>
  String(option?.value || '')
    .toLowerCase()
    .includes(inputValue.toLowerCase());

export const openEditCompanyModal = CommonModalFactory<{
  id?: string;
  detail?: CompanyVo;
}>(
  {
    title: '编辑企业',
    width: 600,
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const [quickParseJson, setQuickParseJson] = useState('');
    const { data: fieldOptions } = useRequest(() => companyApi.fieldOptions());

    const comboOptions = toAutoCompleteOptions(fieldOptions?.combos);
    const customerTypeOptions = toAutoCompleteOptions(fieldOptions?.customerTypes);
    const industryOptions = toAutoCompleteOptions(fieldOptions?.industries);
    const providerOptions = toAutoCompleteOptions(fieldOptions?.providers);

    const handleQuickParse = () => {
      let json: Record<string, any>;
      try {
        json = JSON.parse(quickParseJson);
      } catch (error) {
        message.error('JSON格式不正确，请检查后重试');
        return;
      }
      if (!json || Array.isArray(json) || typeof json !== 'object') {
        message.error('请输入JSON对象');
        return;
      }

      const values: Record<string, any> = {};
      Object.keys(quickParseFieldAliases).forEach((field) => {
        const value = getQuickParseValue(json, field);
        if (value === undefined || value === '') {
          return;
        }
        values[field] = field === 'expireTime' ? dayjs(value) : value;
      });

      form.setFieldsValue(values);
      message.success('解析成功');
    };

    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        const api = data?.id ? companyApi.update : companyApi.create;
        await api({
          companyId: data?.id,
          ...values,
          expireTime: values.expireTime ? dayjs(values.expireTime).format('YYYY-MM-DD') : undefined,
        });
        message.success(data?.id ? '保存成功' : '创建成功');
      });
    }, [data?.id, form, setOkFn]);

    useEffect(() => {
      if (data?.detail) {
        form.setFieldsValue({
          companyName: data.detail.companyName,
          combo: data.detail.combo,
          customerType: data.detail.customerType,
          industry: data.detail.industry,
          provider: data.detail.provider,
          remark: data.detail.remark,
          brand: data.detail.brand,
          spUserId: data.detail.providerInfo?.userId,
          apUserId: data.detail.agentInfo?.userId,
          status: data.detail.status || 'DELIVERING',
          expireTime: data.detail.expireTime ? dayjs(data.detail.expireTime) : undefined,
        });
      } else {
        form.resetFields();
        setQuickParseJson('');
        form.setFieldsValue({
          status: 'DELIVERING',
          expireTime: dayjs().add(1, 'year'),
        });
      }
    }, [data?.detail, form]);

    return (
      <div>
        {!data?.id && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', marginBottom: 16 }}>
            <Input.TextArea
              rows={4}
              value={quickParseJson}
              onChange={(event) => setQuickParseJson(event.target.value)}
              placeholder="请输入企业JSON"
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button type="primary" onClick={handleQuickParse}>
              快速解析
            </Button>
          </div>
        )}
        <Form form={form} layout="vertical">
          <Form.Item label="企业名称" name="companyName" rules={[{ required: true, message: '请输入企业名称' }]}>
            <Input placeholder="请输入企业名称" />
          </Form.Item>
          <Form.Item label="品牌" name="brand" rules={[{ required: true, message: '请输入品牌' }]}>
            <Input placeholder="请输入品牌" />
          </Form.Item>
          <Form.Item label="套餐类型" name="combo" rules={[{ required: true, message: '请输入套餐类型' }]}>
            <AutoComplete
              allowClear
              options={comboOptions}
              filterOption={filterOption}
              placeholder="请选择或输入套餐类型"
            />
          </Form.Item>
          <Form.Item label="客户类型" name="customerType">
            <AutoComplete
              allowClear
              options={customerTypeOptions}
              filterOption={filterOption}
              placeholder="请选择或输入客户类型"
            />
          </Form.Item>
          <Form.Item label="行业" name="industry" rules={[{ required: true, message: '请输入行业' }]}>
            <AutoComplete
              allowClear
              options={industryOptions}
              filterOption={filterOption}
              placeholder="请选择或输入行业"
            />
          </Form.Item>
          <Form.Item label="服务商" name="provider" rules={[{ required: true, message: '请输入服务商' }]}>
            <AutoComplete
              allowClear
              options={providerOptions}
              filterOption={filterOption}
              placeholder="请选择或输入服务商"
            />
          </Form.Item>
          <Form.Item label="企业状态" name="status" rules={[{ required: true, message: '请选择企业状态' }]}>
            <Select placeholder="请选择企业状态" options={companyStatusOptions} />
          </Form.Item>
          <Form.Item label="到期时间" name="expireTime">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="服务商账号" name="spUserId">
            <UserSelector params={{ roleCodes: [EUserRoleCode.SERVICE_PROVIDER] }} placeholder="请选择服务商" />
          </Form.Item>
          <Form.Item label="代理商账号" name="apUserId">
            <UserSelector params={{ roleCodes: [EUserRoleCode.AGENT] }} placeholder="请选择代理商" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea placeholder="请输入备注" rows={4} />
          </Form.Item>
        </Form>
      </div>
    );
  },
  async ({ id } = {}) => {
    if (id) {
      const res = await companyApi.get(id);
      return {
        id: res.companyId,
        detail: res,
      };
    }
    return {
      id: undefined,
      detail: undefined,
    };
  }
);
